/**
 * Split an ANI/ALI caller name (formatted "Last, First") into trimmed first/last
 * parts. ANI/ALI feeds names comma-separated; splitting without trimming leaves a
 * leading space on the first name, so each part is trimmed here.
 */
export function splitCallerName(callerName = ''): { firstName: string; lastName: string } {
    const [lastName = '', firstName = ''] = callerName.split(',').map((part) => part.trim());
    return { firstName, lastName };
}

/** The conditions that together decide whether an opening panel should auto-import. */
type AutoImportCallerInfoConditions = {
    /** The "Automatically import caller name and location on E911 calls" preference is on. */
    isPreferenceEnabled: boolean;
    /** The panel was opened by an incoming E911 call rather than created manually. */
    isE911Origin: boolean;
    /** The event is already saved, so an import would overwrite persisted data. */
    eventExists: boolean;
    /** A phone call has resolved for this panel, so there is caller data to import. */
    hasSelectedCall: boolean;
    /** The panel's initial form load has finished — see the clobber note below. */
    isFormInitialized: boolean;
};

/**
 * Whether the reporting-party caller info should be auto-imported when an event panel
 * opens. Only true for an E911-originated, unsaved draft that has a resolved call, the
 * department preference enabled, and whose form has finished its initial data load — so
 * manual event creation (and any non-E911 draft open) is unaffected regardless of the
 * preference.
 *
 * `isFormInitialized` is required to avoid a clobber: the event panel seeds a new draft
 * via a deferred `eventForm.reset(...)` (see `useIntegratedEventState`) that lands ~a tick
 * after the phone call first resolves via the synchronously-set metadata. Importing before
 * that reset appends a reporting party the reset then wipes (an appended field-array item
 * is not preserved by `keepDirtyValues` when the reset payload has `reportingParties: []`).
 * Gating on the post-reset signal makes the import land after the reset, so it survives.
 */
export function shouldAutoImportCallerInfoOnE911({
    isPreferenceEnabled,
    isE911Origin,
    eventExists,
    hasSelectedCall,
    isFormInitialized,
}: AutoImportCallerInfoConditions): boolean {
    return (
        isPreferenceEnabled && isE911Origin && !eventExists && hasSelectedCall && isFormInitialized
    );
}
